// ---------------------------------------------------------------------------
// Tutorial v2 — the scripted war's Hives, corridors and round-by-round orders
// ---------------------------------------------------------------------------
//
// The fixtures and script for TUTORIAL_V2_DESIGN.md. Nothing here resolves a
// round itself: every round is played through the real rules (submitOrders →
// resolveRound), so the tutorial can't drift from the game. The script only
// decides both sides' orders and which hexes the player may aim at.
//
// Corridors are numbered 1–7 here, as the player sees them.
//
// THE HUNT (enemy Hive). A is a Single in corridor 5. Probe 1 (corridor 3)
// offers three hexes, all 6 from A, so the first stamp always reads A:6.
// Probe 2 (corridor 4) offers six hexes across its two front rows. Every one
// of the 18 aim pairs leaves the two rings crossing at exactly ONE hex — A —
// and A is the nearest structure to every offered aim, so both stamps carry
// the letter A. (Free aim can't do this on a hex grid: rings share whole
// edges. See the design doc.)
//
// After A dies, B (a Rise in corridor 6) is the nearest structure, so the
// kill's stamp points at B. Round 6's strike from corridor 5 damages B
// without killing it; the finale's margin-4 blast from corridor 7 finishes B
// and kills C (a Tall in corridor 7). D–G sit far from all of this.
//
// Factories return fresh state each call — rounds clone, but a caller must
// never share one Hive between two runs.

import { makeHomefront, placeStructure, subIndex, plotOf } from '../rules/subplotGrid'
import { shapeFootprint } from '../rules/homefrontMaker'
import { blankGameBoard } from '../rules/BlankGameBoard'
import { deployArmy, moveUnit, buildFort } from '../rules/gameLogic'
import { BATTLEFIELD_COUNT } from '../rules/gameConfig'
import { submitOrders } from '../rules/submitOrders'
import { resolveRound } from '../rules/resolveRound'
import { makeRevealObserve } from '../rules/revealObserve'

const at = (hf, id, shape, sc, sr) => {
    const fp = shapeFootprint(shape, subIndex(sc, sr))
    if (!fp) throw new Error(`Tutorial Hive: ${shape} at (${sc},${sr}) falls off the Hive.`)
    return placeStructure(hf, id, fp)
}
const hex = (sc, sr) => subIndex(sc, sr)

// The round the scripts' first attacks come in, and the round their copy names.
// A tutorial war holds to it whatever the live rules say, so a retune can't
// break a script (tutorialV3.test.js flags the two drifting apart).
export const TUTORIAL_FIRST_ATTACK_ROUND = 3

// Fixed corridor capacities (a real war draws them from 2–5). Sized so every
// scripted deploy fits on both sides.
export const TUTORIAL_V2_CAPACITIES = [3, 4, 3, 4, 3, 4, 3]

// ── Enemy Hive — the teaching target ────────────────────────────────────────
// Ids sort a < b < … so distance ties resolve toward the lesson's structure.
export function makeTutorialV2OppHive() {
    const hf = makeHomefront(() => null)
    at(hf, 'opp-a', 'single', 12, 4)    // corridor 5 — the hunt's target
    at(hf, 'opp-b', 'rise',   16, 1)    // corridor 6 — nearest once A falls
    at(hf, 'opp-c', 'column', 19, 2)    // corridor 7 — caught by the finale
    at(hf, 'opp-d', 'vee',     2, 9)    // corridor 1
    at(hf, 'opp-e', 'fall',    8, 12)   // corridor 3, back
    at(hf, 'opp-f', 'vee',    16, 12)   // corridor 6, back
    at(hf, 'opp-g', 'single',  4, 13)   // corridor 2, back
    return hf
}

// ── Your Hive ───────────────────────────────────────────────────────────────
// Corridor 7's centre column is kept clear at row 4 for the brood.
export function makeTutorialV2SelfHive() {
    const hf = makeHomefront(() => null)
    at(hf, 'self-s1', 'vee',    4, 2)    // corridor 2, front — the enemy's round-4 strike damages it
    at(hf, 'self-s2', 'column', 16, 2)   // corridor 6
    at(hf, 'self-s3', 'fall',   4, 8)    // corridor 2, middle
    at(hf, 'self-s4', 'single', 7, 10)   // corridor 3
    at(hf, 'self-s5', 'column', 13, 7)   // corridor 5
    at(hf, 'self-s6', 'vee',    19, 12)  // corridor 7, back
    at(hf, 'self-s7', 'single', 1, 13)   // corridor 1, back corner
    return hf
}

// ── The aims the player may choose ──────────────────────────────────────────
export const TUTORIAL_V2_AIMS = {
    probe1: [hex(6, 2), hex(6, 3), hex(6, 4)],                                      // round 3, corridor 3
    probe2: [hex(9, 0), hex(9, 1), hex(10, 0), hex(10, 1), hex(11, 0), hex(11, 1)], // round 3, corridor 4
    kill:   [hex(12, 4)],                                                           // round 4, corridor 5 — where the rings cross
    damage: [hex(14, 1), hex(14, 2)],                                               // round 6, corridor 5
    finale: [hex(18, 2)],                                                           // round 8, corridor 7
}

// The one hex each strike's step points the player at (an arrow on the
// board), from the offered aims above — every one of which the tests prove
// plays the lesson out, so these picks are safe.
export const TUTORIAL_V2_TARGETS = {
    probe1: hex(6, 3),    // A:6
    probe2: hex(11, 1),   // A:3
    kill:   hex(12, 4),
    damage: hex(14, 2),
    finale: hex(18, 2),
}

// ── The script ──────────────────────────────────────────────────────────────
// One entry per round. Per side:
//   deploy     corridors (1–7), one per fresh drone
//   move       { from, dir: 'left' | 'right' }
//   brood      { corridor, row }  (the centre column is the corridor's own)
//   attack     { corridor, aims }  self: the aims offered (key into
//              TUTORIAL_V2_AIMS); opp: the one hex it aims at. A list of them
//              attacks from several corridors in one round.
//   dropZones  corridors
// The enemy only attacks where the lesson needs it; the player is offered
// only the attacks listed.
export const TUTORIAL_V2_SCRIPT = [
    { round: 1,
      self: { deploy: [3, 4] },
      opp:  { deploy: [2, 2] } },
    { round: 2,   // no attacks until round 3: both sides build up
      self: { deploy: [5, 5] },
      opp:  { deploy: [6, 6] } },
    { round: 3,   // both probes at once: the rings cross by the round's end
      self: { attack: [{ corridor: 3, aims: 'probe1' }, { corridor: 4, aims: 'probe2' }], deploy: [7, 7] },
      opp:  { deploy: [6, 6] } },
    { round: 4,   // the kill; meanwhile corridor 2, left empty, is struck
      self: { attack: { corridor: 5, aims: 'kill' }, deploy: [6, 6] },
      opp:  { attack: { corridor: 2, aim: hex(5, 3) }, deploy: [1, 3] } },
    { round: 5,   // block corridor 1 (a 1–1 tie); build the brood
      self: { brood: { corridor: 7, row: 4 }, deploy: [1, 5] },
      opp:  { attack: { corridor: 1, aim: hex(1, 12) }, deploy: [5, 3] } },
    { round: 6,   // move from 6 into 5 to make it 2 against 1
      self: { move: { from: 6, dir: 'left' }, attack: { corridor: 5, aims: 'damage' }, deploy: [7, 7] },
      opp:  { deploy: [3, 4] } },
    { round: 7,   // the brood is mature: its drop zone
      self: { dropZones: [5], deploy: [3, 4] },
      opp:  { deploy: [4, 2] } },
    { round: 8,   // finale: 2 veterans + the brood = 4 against nothing
      self: { attack: { corridor: 7, aims: 'finale' }, deploy: [3, 4] },
      opp:  { deploy: [4, 2] } },
]

// A side's orders for one round, built with the real order functions in the
// order a player could tap them: moves, broods, attacks, then deploys.
// `aimS` is the hex the side attacks (the player's choice, or the enemy's
// scripted aim), or a list of them for a list of attacks.
export function tutorialV2Orders(player, plan, aimS = null) {
    let board = JSON.parse(JSON.stringify(player.board))
    const must = (r, what) => {
        if (!r.success) throw new Error(`Tutorial script: ${what} failed (${r.reason})`)
        return r.boardArr
    }
    if (plan.move) board = must(moveUnit(board, plan.move.from, plan.move.dir), `move from corridor ${plan.move.from}`)
    if (plan.brood) {
        const col = plan.brood.corridor - 1
        board = must(buildFort(board, plan.brood.corridor, subIndex(col * 3 + 1, plan.brood.row)), `brood in corridor ${plan.brood.corridor}`)
    }
    const targetPlots = Array(BATTLEFIELD_COUNT).fill(null)
    const targetSubplots = Array(BATTLEFIELD_COUNT).fill(null)
    ;[plan.attack ?? []].flat().forEach((attack, i) => {
        const col = attack.corridor - 1
        const s = [aimS].flat()[i] ?? attack.aim
        if (s == null) throw new Error(`Tutorial script: corridor ${attack.corridor} attacks with no aim`)
        board = board.map((bf, c) => (c === col ? { ...bf, isAttacking: true } : bf))
        targetSubplots[col] = s
        targetPlots[col] = plotOf(s).row
    })
    let left = plan.deploy?.length ?? 0
    for (const c of plan.deploy ?? []) {
        const r = deployArmy(board, c, left)
        board = must(r, `deploy to corridor ${c}`)
        left = r.armiesToDeploy
    }
    const dropZones = (plan.dropZones ?? []).map(c => c - 1)
    return { board, targetPlots, targetSubplots, dropZones, rockets: [], drones: [] }
}

// A fresh tutorial war, shaped like sim/war.js's: p1 is the player, p2 the
// enemy. `round` is resolveRound's counter (the app's round after both
// submits), so tutorial round 1 resolves as round 2.
export function createTutorialV2War() {
    const player = (homefront) => ({
        board: blankGameBoard(TUTORIAL_V2_CAPACITIES, { firstAttackRound: TUTORIAL_FIRST_ATTACK_ROUND }),
        homefront,
        structuresLost: 0,
        yield: 0,
    })
    return { round: 2, p1: player(makeTutorialV2SelfHive()), p2: player(makeTutorialV2OppHive()) }
}

// Plays script round `index` (0-based): both sides order, submit and the
// round resolves through the real rules. `selfAimS` is the hex the player
// chose (a list, when the round has them attack from several corridors).
// Returns what resolveRound returned
// and the war as it stands for the next round.
export function playTutorialV2Round(war, index, selfAimS = null) {
    const plan = TUTORIAL_V2_SCRIPT[index]
    ;[plan.self.attack ?? []].flat().forEach((attack, i) => {
        const aim = [selfAimS].flat()[i]
        if (!TUTORIAL_V2_AIMS[attack.aims].includes(aim)) throw new Error(`Tutorial round ${plan.round}: hex ${aim} isn't one of the offered aims`)
    })
    return playTutorialV2RoundWith(war, index, tutorialV2Orders(war.p1, plan.self, selfAimS))
}

// The same round with the player's own orders (what they built on the board;
// the step machine only lets them build what the script expects). The enemy
// plays its script. The reveal's per-clash snapshots ride on result.events
// (rules/revealObserve), and `submitted` holds both sides as they went in.
export const playTutorialV2RoundWith = (war, index, selfOrders) => playScriptedRound(TUTORIAL_V2_SCRIPT, war, index, selfOrders)

// Any scripted tutorial's round (v2's, v3's): the player's orders, the
// opponent's from `script`, resolved through the real rules under `config`
// (rules/config.js keys; v3 plays to one kill).
export function playScriptedRound(script, war, index, selfOrders, config = null) {
    const plan = script[index]
    const p1 = submitOrders(war.p1, selfOrders, { config })
    const p2 = submitOrders(war.p2, tutorialV2Orders(war.p2, plan.opp), { config })
    const result = resolveRound({ p1, p2, round: war.round }, { perspective: 'p1', config, observe: makeRevealObserve() })
    return { result, submitted: { p1, p2 }, war: { round: war.round + 1, p1: result.p1, p2: result.p2 } }
}
